import apiClient from '../config/axios';

export interface BranchSellerRelationship {
  id: number;
  relationship_id: string;
  branch_id: number;
  seller_id: number;
  relationship_status: 'active' | 'inactive' | 'suspended';
  last_transaction_date?: string;
  total_order_count: number;
  total_order_value: number;
  createdAt: string;
  updatedAt: string;
}

// Extended interface for populated relationships (with branch and seller data)
export interface BranchSellerRelationshipPopulated extends BranchSellerRelationship {
  branch?: {
    data: {
      id: number;
      attributes: {
        branch_name: string;
        address: string;
        phone: string;
        email: string;
        branch_type: string;
        tax_id: string;
        license_number: string;
        is_active: boolean;
        createdAt: string;
        updatedAt: string;
      };
    };
  };
  seller?: {
    data: {
      id: number;
      attributes: {
        seller_name: string;
        contact_person: string;
        email: string;
        phone: string;
        address: string;
        username: string;
        is_active: boolean;
        createdAt: string;
        updatedAt: string;
      };
    };
  };
}

export interface BranchSellerRelationshipData {
  branch_id: number;
  seller_id: number;
  relationship_status?: 'active' | 'inactive' | 'suspended';
  last_transaction_date?: string;
  total_order_count?: number;
  total_order_value?: number;
}

export interface BranchSellerRelationshipResponse {
  status: string;
  message: string;
  data: {
    id: number;
    attributes: BranchSellerRelationship;
  };
}

export interface BranchSellerRelationshipsListResponse {
  status: string;
  data: Array<{
    id: number;
    attributes: BranchSellerRelationship;
  }>;
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      total: number;
      pageCount: number;
    };
  };
}

class BranchSellerRelationshipService {
  /**
   * Create a new branch-seller relationship (send connection request)
   */
  async createRelationship(relationshipData: BranchSellerRelationshipData): Promise<BranchSellerRelationshipResponse> {
    const response = await apiClient.post('/branch-seller-relationships', relationshipData);
    return response.data;
  }

  /**
   * Get all branch-seller relationships with optional filtering
   */
  async getRelationships(params?: {
    relationship_status?: string;
    branch_id?: number;
    seller_id?: number;
    search?: string;
    page?: number;
    pageSize?: number;
  }): Promise<BranchSellerRelationshipsListResponse> {
    const response = await apiClient.get('/branch-seller-relationships', { params });
    return response.data;
  }

  /**
   * Get relationships by seller ID
   */
  async getRelationshipsBySeller(sellerId: number, params?: {
    page?: number;
    pageSize?: number;
  }): Promise<BranchSellerRelationshipsListResponse> {
    const response = await apiClient.get(`/branch-seller-relationships/seller/${sellerId}`, { params });
    return response.data;
  }

  /**
   * Get relationships by branch ID
   */
  async getRelationshipsByBranch(branchId: number, params?: {
    page?: number;
    pageSize?: number;
  }): Promise<BranchSellerRelationshipsListResponse> {
    const response = await apiClient.get(`/branch-seller-relationships/branch/${branchId}`, { params });
    return response.data;
  }

  /**
   * Get relationships by status
   */
  async getRelationshipsByStatus(status: string, params?: {
    page?: number;
    pageSize?: number;
  }): Promise<BranchSellerRelationshipsListResponse> {
    const response = await apiClient.get(`/branch-seller-relationships/status/${status}`, { params });
    return response.data;
  }

  /**
   * Get a specific relationship by ID
   */
  async getRelationshipById(relationshipId: number): Promise<BranchSellerRelationshipResponse> {
    const response = await apiClient.get(`/branch-seller-relationships/${relationshipId}`);
    return response.data;
  }

  /**
   * Update a relationship (accept/reject connection request)
   */
  async updateRelationship(relationshipId: number, updateData: Partial<BranchSellerRelationship>): Promise<BranchSellerRelationshipResponse> {
    const response = await apiClient.put(`/branch-seller-relationships/${relationshipId}`, updateData);
    return response.data;
  }

  /**
   * Delete a relationship
   */
  async deleteRelationship(relationshipId: number): Promise<BranchSellerRelationshipResponse> {
    const response = await apiClient.delete(`/branch-seller-relationships/${relationshipId}`);
    return response.data;
  }

  /**
   * Update relationship statistics
   */
  async updateRelationshipStats(relationshipId: number, statsData: {
    last_transaction_date?: string;
    total_order_count?: number;
    total_order_value?: number;
  }): Promise<BranchSellerRelationshipResponse> {
    const response = await apiClient.patch(`/branch-seller-relationships/${relationshipId}/stats`, statsData);
    return response.data;
  }

  /**
   * Get relationship statistics summary
   */
  async getRelationshipStats(params?: {
    relationship_status?: string;
    branch_id?: number;
    seller_id?: number;
  }): Promise<any> {
    const response = await apiClient.get('/branch-seller-relationships/stats', { params });
    return response.data;
  }
}

const branchSellerRelationshipService = new BranchSellerRelationshipService();
export default branchSellerRelationshipService;
